<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const tab = ref('radar'); // radar | funnel
const loading = ref(false);
const errorMsg = ref('');
const radar = ref(null);
const funnel = ref(null);
const radarDays = ref(28);
const funnelDays = ref(7);

const SITES = [
  { key: 'modax', label: 'ModaXLarge', domain: 'modaxlarge.com' },
  { key: 'koza', label: 'Koza Butik', domain: 'kozabutik.com' },
  { key: 'adrom', label: 'Adrom (B2B)', domain: 'adromfashion.com' },
];
const site = ref('modax');
const siteDomain = computed(
  () => SITES.find(s => s.key === site.value)?.domain || ''
);

// "?" yardım pencereleri — Reklam Pilotu panelindeki kalıbın aynısı.
const helpKey = ref(null);
const HELP = {
  radar: {
    title: 'Talep Radarı (Search Console)',
    text: "İnsanların Google'a ne yazıp da seçili sitemizi gördüğünü gösterir. Gösterim = arama sonucunda kaç kez göründük; Tık = kaç kişi tıkladı; Pozisyon = ortalama kaçıncı sıradayız (1-10 arası 1. sayfa demektir). Bu veri tamamen bedava trafiğin haritasıdır — reklam harcaması yoktur.",
  },
  treasures: {
    title: '2. Sayfa Hazineleri',
    text: 'Google\'da 5-15. sırada olduğumuz ama ciddi aranan kelimeler. Bunlar "kapıda bekleyen bedava müşteri": 1. sayfaya çıkarsak tıklar katlanır. Çıkış yolu: o kelimeye uygun ürün/kategori sayfasını güçlendirmek (başlık, açıklama, ürün sayısı). En üstteki kelimeler en büyük fırsattır.',
  },
  pages: {
    title: 'Aranan Sayfalar',
    text: 'Google aramasından en çok ziyaret alan sayfalarımız. Hangi ürün/kategori organik ilgi görüyorsa orada gerçek talep var demektir — o ürünler reklam ve stok kararlarında öne alınabilir.',
  },
  funnel: {
    title: 'Gerçek Huni (GA4)',
    text: "Google Analytics bağımsız hakemdir: her kanaldan (Meta reklamı, Instagram profili, Google araması, direkt) kaç ziyaret geldi, kaçı sepete ekledi, kaçı satın aldı, ne ciro oldu. Meta'nın kendi raporuyla buradaki rakamlar birebir tutmayabilir (ölçüm farkı normaldir) — önemli olan kanalların birbirine ORANI ve gidişatı.",
  },
  trend: {
    title: 'Günlük Gidişat',
    text: 'Son 28 günün günlük ziyaret ve satış çizgisi. Reklam değişikliklerinin (yeni kampanya, bütçe artışı) siteye yansımasını buradan izlersin: kampanya açıldığı gün ziyaret yükseliyor mu, satış onu takip ediyor mu?',
  },
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    if (tab.value === 'radar' && !radar.value) {
      radar.value = await fetchMarketplaceJson(
        accountId,
        `google_stack/radar?days=${radarDays.value}&site=${site.value}`
      );
    }
    if (tab.value === 'funnel' && !funnel.value) {
      funnel.value = await fetchMarketplaceJson(
        accountId,
        `google_stack/funnel?days=${funnelDays.value}&site=${site.value}`
      );
    }
  } catch (e) {
    errorMsg.value = 'Google verisi yüklenemedi — bağlantıyı kontrol edelim.';
  } finally {
    loading.value = false;
  }
};

const switchTab = t => {
  tab.value = t;
  load();
};

const switchSite = async s => {
  if (site.value === s) return;
  site.value = s;
  radar.value = null;
  funnel.value = null;
  await load();
};

const reloadRadar = async days => {
  radarDays.value = days;
  radar.value = null;
  await load();
};

const reloadFunnel = async days => {
  funnelDays.value = days;
  funnel.value = null;
  await load();
};

const CHANNEL_TR = {
  'Paid Social': '📣 Ücretli Sosyal (Meta reklamları)',
  'Organic Social': '🌱 Organik Sosyal (IG profili)',
  'Organic Search': '🔍 Organik Arama (Google)',
  'Organic Shopping': '🛍 Organik Shopping',
  Direct: '🎯 Direkt (adresi yazan)',
  Referral: '🔗 Başka siteden',
  'Paid Other': '💰 Diğer ücretli',
  Unassigned: '❓ Atanmamış',
  'AI Assistant': '🤖 Yapay zeka asistanları',
  Email: '✉️ E-posta',
};

const maxTrendRevenue = computed(() =>
  Math.max(...(funnel.value?.trend || []).map(t => t.revenue), 1)
);

const trDate = d => `${d.slice(6, 8)}.${d.slice(4, 6)}`;

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto px-6 py-8">
      <!-- Başlık -->
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-widest text-n-slate-10"
          >
            {{ siteDomain }} — Google İstihbaratı
          </div>
          <h1 class="text-3xl font-semibold text-n-slate-12 mt-1">🔵 Google</h1>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="s in SITES"
            :key="s.key"
            class="rounded-full px-4 py-2 text-sm font-semibold border"
            :class="
              site === s.key
                ? 'bg-n-solid-3 border-n-strong text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="switchSite(s.key)"
          >
            {{ s.label }}
          </button>
          <span class="w-px h-6 bg-n-weak mx-1" />
          <button
            class="rounded-full px-4 py-2 text-sm font-semibold border"
            :class="
              tab === 'radar'
                ? 'bg-woot-600 border-woot-600 text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="switchTab('radar')"
          >
            📡 Talep Radarı
          </button>
          <button
            class="rounded-full px-4 py-2 text-sm font-semibold border"
            :class="
              tab === 'funnel'
                ? 'bg-woot-600 border-woot-600 text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="switchTab('funnel')"
          >
            🕳 Gerçek Huni
          </button>
        </div>
      </div>

      <div
        v-if="errorMsg"
        class="rounded-lg bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-4 py-3 mb-4 text-sm"
      >
        {{ errorMsg }}
      </div>
      <div v-if="loading" class="text-n-slate-11 py-10 text-center">
        Google'dan veri çekiliyor…
      </div>

      <!-- ============ TALEP RADARI ============ -->
      <template v-if="tab === 'radar' && radar && !loading">
        <div class="flex items-center gap-2 mb-6 text-sm">
          <span class="text-n-slate-10">Dönem:</span>
          <button
            v-for="d in [7, 28, 90]"
            :key="d"
            class="rounded-full border px-3 py-1"
            :class="
              radarDays === d
                ? 'bg-n-solid-3 border-n-strong font-bold text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="reloadRadar(d)"
          >
            {{ d }} gün
          </button>
        </div>

        <!-- 2. Sayfa Hazineleri -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            💎 2. Sayfa Hazineleri
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'treasures'"
          >
            ?
          </button>
        </div>
        <div
          class="rounded-2xl border-2 border-yellow-500 bg-n-solid-1 overflow-hidden mb-8"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-10 text-left">
                  <th class="px-4 py-2">Arama kelimesi</th>
                  <th class="px-4 py-2 text-right">Aranma (gösterim)</th>
                  <th class="px-4 py-2 text-right">Tık</th>
                  <th class="px-4 py-2 text-right">Sıramız</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="q in radar.treasures"
                  :key="q.query"
                  class="border-t border-n-weak"
                >
                  <td class="px-4 py-2 font-semibold text-n-slate-12">
                    {{ q.query }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ q.impressions.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ q.clicks }}
                  </td>
                  <td
                    class="px-4 py-2 text-right tabular-nums font-bold text-yellow-700 dark:text-yellow-400"
                  >
                    {{ q.position }}
                  </td>
                </tr>
                <tr v-if="!radar.treasures.length">
                  <td colspan="4" class="px-4 py-4 text-n-slate-10">
                    Bu dönemde 5-15. sıra bandında ciddi aranan kelime yok.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- En çok tıklanan aramalar -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🔍 En Çok Tık Getiren Aramalar
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'radar'"
          >
            ?
          </button>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden mb-8"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-10 text-left">
                  <th class="px-4 py-2">Arama kelimesi</th>
                  <th class="px-4 py-2 text-right">Gösterim</th>
                  <th class="px-4 py-2 text-right">Tık</th>
                  <th class="px-4 py-2 text-right">CTR %</th>
                  <th class="px-4 py-2 text-right">Sıramız</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="q in radar.top_queries"
                  :key="q.query"
                  class="border-t border-n-weak"
                >
                  <td class="px-4 py-2 text-n-slate-12">{{ q.query }}</td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ q.impressions.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums font-semibold">
                    {{ q.clicks }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">{{ q.ctr }}</td>
                  <td
                    class="px-4 py-2 text-right tabular-nums"
                    :class="
                      q.position <= 10
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-yellow-700 dark:text-yellow-400'
                    "
                  >
                    {{ q.position }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Aranan sayfalar -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            📄 Aramadan Ziyaret Alan Sayfalar
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'pages'"
          >
            ?
          </button>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-10 text-left">
                  <th class="px-4 py-2">Sayfa</th>
                  <th class="px-4 py-2 text-right">Gösterim</th>
                  <th class="px-4 py-2 text-right">Tık</th>
                  <th class="px-4 py-2 text-right">Sıra</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in radar.pages"
                  :key="p.page"
                  class="border-t border-n-weak"
                >
                  <td class="px-4 py-2 text-n-slate-12 max-w-md truncate">
                    {{ p.page }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ p.impressions.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums font-semibold">
                    {{ p.clicks }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ p.position }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <!-- ============ GERÇEK HUNİ ============ -->
      <template v-if="tab === 'funnel' && funnel && !loading">
        <div class="flex items-center gap-2 mb-6 text-sm">
          <span class="text-n-slate-10">Dönem:</span>
          <button
            v-for="d in [7, 14, 28]"
            :key="d"
            class="rounded-full border px-3 py-1"
            :class="
              funnelDays === d
                ? 'bg-n-solid-3 border-n-strong font-bold text-n-slate-12'
                : 'border-n-weak text-n-slate-11'
            "
            @click="reloadFunnel(d)"
          >
            {{ d }} gün
          </button>
        </div>

        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            🕳 Kanal Bazlı Gerçek Huni
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'funnel'"
          >
            ?
          </button>
        </div>
        <div
          v-if="site === 'koza'"
          class="rounded-lg bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-4 py-3 mb-4 text-sm"
        >
          ⚠️ Koza'da satış (purchase) ölçümü henüz akmıyor — Ticimax panelinden
          GA4 e-ticaret entegrasyonu açılınca dolacak. Ziyaret ve kanal verileri
          gerçektir; satış sütununun 0 görünmesi "satış yok" demek değildir.
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden mb-8"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-solid-2 text-n-slate-10 text-left">
                  <th class="px-4 py-2">Kanal</th>
                  <th class="px-4 py-2 text-right">Ziyaret</th>
                  <th class="px-4 py-2 text-right">Sepete Ekleme</th>
                  <th class="px-4 py-2 text-right">Satış</th>
                  <th class="px-4 py-2 text-right">Ciro (TL)</th>
                  <th class="px-4 py-2 text-right">Dönüşüm %</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="c in funnel.channels"
                  :key="c.channel"
                  class="border-t border-n-weak"
                >
                  <td class="px-4 py-2 font-semibold text-n-slate-12">
                    {{ CHANNEL_TR[c.channel] || c.channel }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ c.sessions.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums">
                    {{ c.add_to_carts.toLocaleString('tr-TR') }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums font-semibold">
                    {{ c.purchases }}
                  </td>
                  <td class="px-4 py-2 text-right tabular-nums font-bold">
                    {{ c.revenue.toLocaleString('tr-TR') }}
                  </td>
                  <td
                    class="px-4 py-2 text-right tabular-nums"
                    :class="
                      c.sessions > 50 && c.purchases / c.sessions < 0.005
                        ? 'text-red-600 dark:text-red-400 font-bold'
                        : 'text-n-slate-11'
                    "
                  >
                    {{
                      c.sessions > 0
                        ? ((c.purchases / c.sessions) * 100).toFixed(2)
                        : '—'
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">
            📈 Günlük Gidişat (28 gün)
          </h2>
          <button
            class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold"
            @click="helpKey = 'trend'"
          >
            ?
          </button>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
          <div class="flex items-end gap-[3px] h-40">
            <div
              v-for="t in funnel.trend"
              :key="t.date"
              class="flex-1 flex flex-col items-center gap-1 group relative"
            >
              <div
                class="w-full rounded-t bg-woot-500 group-hover:bg-woot-600 transition-all"
                :style="{
                  height: `${Math.max((t.revenue / maxTrendRevenue) * 100, 2)}%`,
                }"
              />
              <div
                class="hidden group-hover:block absolute -top-14 bg-n-solid-3 border border-n-weak rounded-lg px-2 py-1 text-xs whitespace-nowrap z-10"
              >
                {{ trDate(t.date) }}: {{ t.revenue.toLocaleString('tr-TR') }} TL
                · {{ t.purchases }} satış · {{ t.sessions }} ziyaret
              </div>
            </div>
          </div>
          <div class="flex justify-between text-xs text-n-slate-10 mt-2">
            <span>{{
              funnel.trend.length ? trDate(funnel.trend[0].date) : ''
            }}</span>
            <span>ciro (TL/gün) — çubuğun üstüne gel, detayı gör</span>
            <span>{{
              funnel.trend.length
                ? trDate(funnel.trend[funnel.trend.length - 1].date)
                : ''
            }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- "?" AÇIKLAMA PENCERESİ -->
    <div
      v-if="helpKey"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6"
      @click.self="helpKey = null"
    >
      <div
        class="max-w-xl w-full rounded-2xl bg-n-solid-1 border border-n-weak p-6 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4 mb-3">
          <h3 class="text-2xl font-semibold text-n-slate-12">
            💡 {{ HELP[helpKey].title }}
          </h3>
          <button
            class="rounded-full border border-n-weak px-3 py-1 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-3"
            @click="helpKey = null"
          >
            Kapat ✕
          </button>
        </div>
        <p class="text-lg leading-relaxed text-n-slate-12">
          {{ HELP[helpKey].text }}
        </p>
      </div>
    </div>
  </div>
</template>
